import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ApproveRequestDialogIcon from "@entrilia/shared/icons/ApproveRequestDialogIcon";
import CancelRequestDialogIcon from "@entrilia/shared/icons/CancelRequestDialogIcon";
import RejectRequestDialogIcon from "@entrilia/shared/icons/RejectRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Approval } from "../../api/types/approvalTypes";
import ObjectDuplicatesCheck from "../entityFields/ObjectDuplicatesCheck";
import useObjectDuplicates from "../entityFields/useObjectDuplicates";
import { getPendingApprovalSuccessMessage } from "./approvalsHelper";
import useWorkflowActionChecks from "./workflows/useWorkflowActionChecks";
import WorkflowActionChecks from "./workflows/WorkflowActionChecks";

type ApprovalAction = "approve" | "reject" | "cancel";

const workflowSignalByAction: Record<ApprovalAction, WorkflowSignalType> = {
  approve: "Approve",
  reject: "Reject",
  cancel: "Cancel",
};

const approveApproval = withErrorHandling(adminApi.approveApproval);
const rejectApproval = withErrorHandling(adminApi.rejectApproval);
const cancelApproval = withErrorHandling(adminApi.cancelApproval);

const approveWayOut = "Fix these, then approve. If you can't, reject it and say what needs fixing.";

interface DialogTexts {
  title: string;
  prompt: string;
  description?: string;
  buttonText: string;
  buttonVariant: "contained" | "outlined" | "text";
  buttonColor: "primary" | "error";
  successMessage: string;
}

const getDialogTexts = (action: ApprovalAction, itemName: string): DialogTexts => {
  switch (action) {
    case "approve":
      return {
        title: "Approve Request",
        prompt: `Approve ${itemName}?`,
        buttonText: "Approve",
        buttonVariant: "contained",
        buttonColor: "primary",
        successMessage: "Request has been approved",
      };
    case "reject":
      return {
        title: "Reject Request",
        prompt: `Reject ${itemName}?`,
        buttonText: "Reject",
        buttonVariant: "outlined",
        buttonColor: "error",
        successMessage: "Request has been rejected",
      };
    case "cancel":
      return {
        title: "Cancel Request",
        prompt: `Cancel ${itemName}?`,
        description:
          "This action will cancel your approval request. You can make edits and resubmit for approval at any time.",
        buttonText: "Cancel Request",
        buttonVariant: "outlined",
        buttonColor: "error",
        successMessage: "Request has been canceled",
      };
    default:
      return {
        title: action,
        prompt: action,
        buttonText: action,
        buttonVariant: "contained",
        buttonColor: "primary",
        successMessage: "Success",
      };
  }
};

interface Props {
  action: ApprovalAction;
  approval: Pick<Approval, "id" | "itemName" | "objectType" | "objectId" | "workflowId">;
  showActionChecks?: boolean;
  onClose: () => void;
  onApply: (updatedApproval: Approval) => void;
}

const ApprovalActionDialog = ({ action, approval, showActionChecks, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");

  const actionChecks = useWorkflowActionChecks(
    showActionChecks && action === "approve" && approval.workflowId
      ? { workflowId: approval.workflowId, action: "Approve" }
      : undefined
  );
  const duplicates = useObjectDuplicates(
    approval.objectType && approval.objectId
      ? { objectType: approval.objectType, objectId: approval.objectId, action: workflowSignalByAction[action] }
      : undefined
  );

  const handleApply = async () => {
    const apiCall = action === "approve" ? approveApproval : action === "reject" ? rejectApproval : cancelApproval;

    setSaving(true);
    const [updatedApproval, error] = await apiCall(approval.id, { commentUserInput: comment.trim() || undefined });
    setSaving(false);

    if (error) {
      logError(error, `[ApprovalActionDialog] ${action}Approval`);
      sendNotificationError(error.message);
      return;
    }

    sendNotification(
      updatedApproval.status === "Pending" ? getPendingApprovalSuccessMessage(updatedApproval) : successMessage
    );
    onApply(updatedApproval);
    onClose();
  };

  const { title, prompt, description, buttonText, buttonVariant, buttonColor, successMessage } = getDialogTexts(
    action,
    approval.itemName
  );

  const { objectType } = approval;

  const isCommentRequired = action === "reject";
  const isValid = !isCommentRequired || comment.trim().length > 0;

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          {action === "approve" && <ApproveRequestDialogIcon sx={{ width: 80, height: 80 }} />}
          {action === "reject" && <RejectRequestDialogIcon sx={{ width: 80, height: 80 }} />}
          {action === "cancel" && <CancelRequestDialogIcon sx={{ width: 80, height: 80 }} />}

          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {prompt}
            </Typography>
            {description && (
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                {description}
              </Typography>
            )}
          </Stack>

          <WorkflowActionChecks
            action="Approve"
            blockers={actionChecks.blockers}
            warnings={actionChecks.warnings}
            hasFailed={actionChecks.hasFailed}
            hint={approveWayOut}
          />

          {objectType && <ObjectDuplicatesCheck objectType={objectType} objectDuplicates={duplicates} />}

          <TextField
            fullWidth
            multiline
            rows={5}
            label={
              isCommentRequired ? (
                <Typography>
                  Comment
                  <RequiredFieldMarker />
                </Typography>
              ) : (
                "Comment (optional)"
              )
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant={buttonVariant}
          color={buttonColor}
          loading={saving || actionChecks.isFetching || duplicates.isFetching}
          onClick={handleApply}
          disabled={!isValid || actionChecks.blockers.length > 0}
        >
          {buttonText}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ApprovalActionDialog;
